import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MiniFooter } from '@app/src/common/MiniFooter';
import { ButtonsAction } from '@app/src/common/ButtonsAction';
import {
  PriceConceptWrapper,
  ButtonPrevWrapper,
  ButtonNextWrapper,
  ButtonActionsWrapper,
  Wrapper
} from './styled';
import { isClientEnv } from '@app/src/utils/validations';
import { useHistory, useParams } from 'react-router-dom';
import { fetchValidate } from '@app/src/common/session';
import { COLORS } from '@app/src/ui';
import { fetchProcessConfirmConsume } from '@app/src/views/StepConfirm/sectionConfirm';

const isClient = isClientEnv();
const BREAKPOINT = 768;

interface Props {
  categoryConfig?: any;
  priceTotal?: number;
}

export const FormActions: React.FC<Props> = ({ categoryConfig, priceTotal }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { idPrint, packageId } = useParams();
  const [isFixedActionsButtons, setIsFixedActionsButtons] = useState(false);
  let active = false;
  const [isDisabledScroll, setIsDisabledScroll] = useState(false);
  const refWrapper = useRef(null);
  const refFooter = useRef(null);
  const prints = useSelector(state => state.summary.data.print);
  const userStorage = isClient && JSON.parse(localStorage.getItem('user'));
  const [isPayment, setIsPayment] = useState(false);
  const validate = useSelector(state => state.session.validate.data);
  const isLoadingValidate = useSelector(state => state.session.validate.isFetching);
  const userData = useSelector(state => state.session.user.data);
  const printData = isClient && JSON.parse(sessionStorage.getItem('print'));
  const typeUserAction = useSelector(state => state.session.user.pasarella);
  const confirmData = useSelector(state => state.processConfirm.data);
  const isLoadingConfirmData = useSelector(state => state.processConfirm.isFetching);

  useEffect(() => {
    if (isClient) {
      if (window.innerWidth < BREAKPOINT) {
        window.addEventListener('scroll', handleScroll);
      }
      window.addEventListener('resize', handleResizeWindow);
      return () => {
        window.removeEventListener('resize', handleResizeWindow);
        if (window.innerWidth < BREAKPOINT) {
          window.removeEventListener('scroll', handleScroll);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (userStorage && userStorage.jwt && validate && validate.email && isPayment) {
      if (typeUserAction === 'consume-print') {
        dispatch(fetchProcessConfirmConsume({
          printDetail: idPrint,
          category: printData && printData.general && printData.general.category,
          user: userStorage && userStorage.data && userStorage.data.id, categoryConfig,
          package: packageId
        }))
      }
      if (typeUserAction === 'purchase-print') {
        history.push(`${categoryConfig.paths.purchasePrint.stepFacturation}/${idPrint}`);
      }
    }
  }, [validate]);

  useEffect(() => {
    if (confirmData && Object.keys(confirmData).length > 0) {
      history.push(categoryConfig.paths.consumePackage.stepPrintPublished);
    }
  }, [confirmData]);

  const handleResizeWindow = useCallback(() => {
    if (window.innerWidth >= BREAKPOINT) {
      setIsFixedActionsButtons(false);
      window.removeEventListener('scroll', handleScroll)
    } else {
      window.addEventListener('scroll', handleScroll)
      if (active) {
        active = false;
      }
    }
  }, [dispatch]);

  const disabledScroll = () => {
    setIsDisabledScroll(false)
    setTimeout(() => {
      setIsDisabledScroll(true)
    }, 500)
  }

  const handleScroll = useCallback(() => {
    const bodyHeight = document.body.offsetHeight;
    const fixedElementHeight = refWrapper.current.offsetHeight;
    const footerHeight = refFooter.current.offsetHeight;

    if (!isDisabledScroll) {
      if (bodyHeight - (window.pageYOffset + window.innerHeight) > fixedElementHeight && active === false) {
        active = true;
        setIsFixedActionsButtons(true);
        disabledScroll();
      }
      if (bodyHeight - (window.pageYOffset + window.innerHeight) <= footerHeight && active === true) {
        active = false;
        setIsFixedActionsButtons(false)
        disabledScroll()
      }
    }
  }, [dispatch])

  const handleClickGoBack = () => {
    if (typeUserAction === 'consume-print') {
      history.push(`${categoryConfig.paths.consumePackage.stepTypePublication}/${packageId}`);
    }
    if (typeUserAction === 'purchase-print') {
      history.push(categoryConfig.paths.purchasePrint.stepTypePublication);
    }
  }

  const handleClickNext = () => {
    pushDatalayer(categoryConfig.name);
    setIsPayment(true);
    const updateUser = {
      ...userStorage,
      categoryId: printData && printData.general && printData.general.category,
      user: userData && userData.id,
      uniqueId: idPrint,
      payment: typeUserAction
    };
    isClient && localStorage.setItem('user', JSON.stringify(updateUser));
    if (userStorage && userStorage.jwt) {
      dispatch(fetchValidate());
    } else {
      if (typeUserAction === 'purchase-print') {
        history.push('/inicia-tu-sesion', { from: 'purchase-print' });
      } else {
        history.push('/inicia-tu-sesion');
      }
    }
  }

  const pushDatalayer = (categoryName: string) => {
    let printsTotal = 0;
    Object.keys(prints).forEach(key => {
      printsTotal = printsTotal + prints[key].dates.length;
    })
    if (isClient && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        'event': 'EVClasificados',
        'eventCategory': 'Posting',
        'eventAction': `Paso 3 - ${categoryName}`,
        'eventLabel': `${printsTotal}|S/ ${priceTotal}`,
        'eventValue': 1
      })
    }
  };

  const setButtonNextText = () => {
    if (typeUserAction === 'consume-print') {
      return 'Publicar';
    }
    return 'Siguiente';
  };

  return (
    <Wrapper ref={refWrapper}>
      <ButtonActionsWrapper fixed={isFixedActionsButtons} >
        {
          priceTotal ?
            <PriceConceptWrapper
              isDisplay={isFixedActionsButtons}
              bgColor={`rgba(${categoryConfig && categoryConfig.colorRgba}, .2)`}
              priceTotal={priceTotal} /> : null
        }
        <ButtonsAction
          ButtonPrev={
            <ButtonPrevWrapper
              handleClick={handleClickGoBack}
              background='white'
              color={COLORS.BLACK_BOLD}
              width={160}
              text="¿Dónde quieres publicar?"
              icon={{ name: 'icon-back', color: COLORS.BLACK_BOLD, width: '16' }}
              left
            />}
          ButtonNext={
            <ButtonNextWrapper
              handleClick={handleClickNext}
              background={COLORS.BLACK_BOLD}
              color="white"
              text={setButtonNextText()}
              width={140}
              icon={{ name: 'icon-back', color: '#fff', width: '16' }}
              loading={isLoadingValidate || isLoadingConfirmData}
              disabled={isLoadingValidate || isLoadingConfirmData}
            />}
        />
      </ButtonActionsWrapper>
      <MiniFooter refFooter={refFooter} />
    </Wrapper>
  )
};
